import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const STEPS = [
  {
    n: "01",
    title: "Free consultation",
    body: "We review your profile, travel plans and previous visa history to provide a clear and honest assessment of your application.",
  },
  {
    n: "02",
    title: "Document preparation",
    body: "We create your personalized checklist and help organize your financial documents, bookings, travel insurance, cover letter and other required paperwork.",
  },
  {
    n: "03",
    title: "Appointment booking",
    body: "We secure the earliest submission slot based on your availability.",
  },
  {
    n: "04",
    title: "Application submission",
    body: "We guide you through the biometrics and submission process, helping ensure your application is complete and nothing important is overlooked.",
  },
];

export const HowItWorks = ({ onNavigate }) => (
  <section
    id="how-it-works"
    data-testid="how-it-works-section"
    className="px-6 md:px-12 pt-28 md:pt-36 pb-16 md:pb-20"
  >
    <div className="max-w-[1400px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-8 mb-16">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="text-xs font-semibold tracking-[0.25em] uppercase text-slate-500 mb-5"
          >
            How it works
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900"
          >
            Your visa, in{" "}
            <em className="italic text-slate-500">four steps.</em>
          </motion.h2>
        </div>
        <motion.button
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          onClick={() => onNavigate("#contact")}
          data-testid="how-it-works-cta"
          className="group flex items-center gap-2 text-sm font-semibold text-slate-900 border-b border-slate-900 pb-1 hover:text-slate-500 hover:border-slate-400 transition-colors duration-300"
        >
          Start with step one - it's free
          <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </motion.button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-14">
        {STEPS.map((s, i) => (
          <motion.div
            key={s.n}
            data-testid={`how-it-works-step-${s.n}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: i * 0.12, ease: EASE }}
            className="border-t border-slate-300 pt-8"
          >
            <span className="font-display text-6xl text-slate-200 leading-none">{s.n}</span>
            <h3 className="font-display text-xl sm:text-2xl tracking-tight text-slate-900 mt-6">
              {s.title}
            </h3>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.body}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
